// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { webhooksApi, type CreateWebhookPayload } from '@/lib/api/webhooks'
import { queryKeys } from '@/constants/queryKeys'

export function useWebhookList() {
  return useQuery({
    queryKey: queryKeys.webhooks.list(),
    queryFn: () => webhooksApi.list(),
  })
}

export function useWebhook(id: string) {
  return useQuery({
    queryKey: queryKeys.webhooks.detail(id),
    queryFn: () => webhooksApi.getById(id),
    enabled: !!id,
  })
}

export function useWebhookLogs(id: string) {
  return useQuery({
    queryKey: queryKeys.webhooks.logs(id),
    queryFn: () => webhooksApi.getDeliveryLogs(id),
    enabled: !!id,
  })
}

export function useCreateWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateWebhookPayload) => webhooksApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.all() })
    },
  })
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Parameters<typeof webhooksApi.update>[1]) =>
      webhooksApi.update(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.all() })
    },
  })
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => webhooksApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.all() })
    },
  })
}
